import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import useBelowBreakpoint from "core-web/hooks/useBelowBreakpoint";
import showOverlay from "core-web/state/models/Overlay/dispatches/showOverlay";
import getProductStockStatus from "core-web/utils/getProductStockStatus";
import hexWithOpacity from "core-web/utils/hexWithOpacity";
import styleObjectToProps from "core-web/utils/styleObjectToProps";
import FormatPrice from "core-web/components/FormatPrice";
import Button from "core-web/components/base/Button";
import colors from "theme/config/colors";
import styled from "theme/libs/styled";
import Loading from "theme/components/Loading";
import CheckIcon from "theme/components/icons/CheckIcon";
import PlusIcon from "theme/components/icons/PlusIcon";
import TextStyles from "theme/components/text/TextStyles";

const StyledButton = styled(Button)`
    border-radius: 40px;
    position: relative;
    top: 0;
    right: 0;
    height: 40px;
    max-width: 40px;
    padding-right: 16px;
    z-index: 2;
    transition:
        min-width ${({ theme }) => theme.transitions.secondaryFast},
        max-width ${({ theme }) => theme.transitions.secondaryFast};
    color: ${({ theme }) => theme.colors.black};
    background-color: ${({ theme }) => theme.colors.lightGreige};

    &[data-is-variant="true"] {
        cursor: not-allowed;
        max-width: 200px;
        color: ${({ theme }) => theme.colors.white};
        background-color: ${({ theme }) => hexWithOpacity(theme.colors.red, 50)};
    }

    &[data-is-loading="true"] {
        max-width: 48px;
        padding-left: 16px;
    }

    &[data-is-variant-selected="true"] {
        cursor: pointer;
        max-width: 200px;
        color: ${({ theme }) => theme.colors.white};
        background-color: ${({ theme }) => theme.colors.red};
        pointer-events: initial;
    }

    &[data-is-disabled="true"] {
        background-color: ${({ theme }) => hexWithOpacity(theme.colors.red, 50)};
        cursor: not-allowed;
    }

    ${({ theme }) => theme.media.hover} {
        &[data-is-variant="false"]:hover,
        &[data-is-variant-selected="true"]:hover {
            &[data-is-disabled="false"] {
                max-width: 200px;
                opacity: 1;
                color: ${({ theme }) => theme.colors.white};
                background-color: ${({ theme }) => theme.colors.red};

                & path {
                    stroke: white !important;
                }

                & .loading circle {
                    stroke: white !important;
                }
            }
        }
    }

    &[data-is-added="true"] {
        max-width: 200px;
        opacity: 1;
        color: ${({ theme }) => theme.colors.white};
        background-color: ${({ theme }) => theme.colors.red};

        & path {
            stroke: white !important;
        }

        & .loading circle {
            stroke: white !important;
        }
    }

    &[data-is-added="true"] path {
        & path {
            stroke: black !important;
        }
    }

    ${({ theme }) => theme.below.tabletLg} {
        &[data-is-active="false"] {
            cursor: pointer;
            max-width: 40px;
            color: ${({ theme }) => theme.colors.black};
            background-color: ${({ theme }) => theme.colors.lightGreige};
        }
    }
`;

const Flex = styled("div")`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    position: relative;
    height: 100%;
    width: 100%;
`;

const BuyText = styled("span")`
    margin: 0 16px;
    white-space: nowrap;
    opacity: 0;
    transition: opacity 100ms ease;
    pointer-events: none;

    &[data-is-added="true"],
    &[data-is-active="true"] {
        transition: opacity 100ms ease 100ms;
        opacity: 1;
    }

    .quick-shop-btn[data-is-variant="true"] & {
        opacity: 1;
    }

    ${({ theme }) => theme.media.hover} {
        .quick-shop-btn:hover & {
            transition: opacity 100ms ease 100ms;
            opacity: 1;
        }
    }
    ${({ theme }) => theme.below.tabletLg} {
        .quick-shop-btn[data-is-active="false"] & {
            opacity: 0;
        }
    }
`;

const IconWrapper = styled("div")`
    position: absolute;
    right: -3px;

    ${({ theme }) => theme.above.tabletLg} {
        top: 11px;
    }
`;

const textStyles = styleObjectToProps(["Buenos Aires/14-120 Regular", null, null, "Buenos Aires/14-120 Regular"]);

const RelationQuickShopButton = ({
    handleClick,
    isAdded,
    isLoading,
    isVariant,
    selectedParametrics,
    isVariantSelected,
    priceExVat,
    vatRate,
    product,
    relationsVariant,
    quantity,
}) => {
    const { on_hand: onHand } = isVariant ? relationsVariant || {} : product;
    const { t } = useTranslation();
    const [isActive, setIsActive] = useState(false);
    const remainingChoice = selectedParametrics.find((param) => param.value === false);
    const stockStatus = onHand ? getProductStockStatus(onHand) : null;
    const stock = stockStatus?.stock;
    const isBelowTabletLg = useBelowBreakpoint("tabletLg");

    const conditionText = isAdded
        ? "added"
        : stock <= 0
          ? "choose"
          : !isVariant || isVariantSelected
            ? "buy"
            : "choose";

    const buttonText = t(`pdp.${conditionText}`, {
        variant: stock <= 0 ? selectedParametrics[0]?.name : remainingChoice?.name,
    });

    const priceProps = {
        price: priceExVat,
        vatRate,
        separateWith: " ",
        render: (price) => t("price_with_currency", { price }),
    };

    useEffect(() => {
        priceProps.price = priceExVat;
    }, [priceExVat]);

    const handleBuyClick = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (isBelowTabletLg) {
            if (isVariant) {
                showOverlay("relations_overlay", { product, quantity });
            } else if (!isActive) {
                setIsActive(true);
            }
        }

        if (!stock && isVariant) {
            return;
        }
        handleClick(e);
    };

    return (
        <StyledButton
            className="quick-shop-btn"
            data-is-added={isAdded}
            data-is-loading={isLoading}
            data-is-variant={isVariant}
            data-is-variant-selected={isVariantSelected}
            data-is-disabled={!stock && isVariant}
            data-is-active={isActive}
            onClick={handleBuyClick}
        >
            <Flex>
                {isVariantSelected && !isLoading && !!stock && (
                    <TextStyles as="span" textStyles="Buenos Aires/14-120 Regular" minWidth="max-content" ml="16px">
                        <FormatPrice {...priceProps} />
                    </TextStyles>
                )}
                {isLoading ? (
                    <Loading className="loading" width="16px" height="16px" />
                ) : (
                    <>
                        <BuyText data-is-added={isAdded} {...textStyles}>
                            {buttonText}
                        </BuyText>
                        <IconWrapper>
                            {isAdded ? (
                                <CheckIcon width="12px" height="12px" color={colors.black} />
                            ) : (
                                <PlusIcon width="12px" height="12px" />
                            )}
                        </IconWrapper>
                    </>
                )}
            </Flex>
        </StyledButton>
    );
};

RelationQuickShopButton.propTypes = {
    handleClick: PropTypes.func,
    isAdded: PropTypes.bool,
    isLoading: PropTypes.bool,
    isOpen: PropTypes.bool,
    isVariant: PropTypes.bool.isRequired,
    isVariantSelected: PropTypes.bool,
    priceExVat: PropTypes.number,
    product: PropTypes.object,
    quantity: PropTypes.number.isRequired,
    relationsVariant: PropTypes.object,
    selectedParametrics: PropTypes.array,
    vatRate: PropTypes.number,
};

RelationQuickShopButton.defaultProps = {
    handleClick: () => null,
    isAdded: false,
    isLoading: false,
    isOpen: false,
    isVariantSelected: false,
    priceExVat: null,
    product: {},
    relationsVariant: {},
    selectedParametrics: [],
    vatRate: null,
};

export default RelationQuickShopButton;
